import { useEffect } from 'react';

type VisibilityGuardOptions = {
  enabled: boolean;
  sectionRef: React.RefObject<HTMLElement | null>;
  onExit: () => void;
  threshold?: number;
  rootMargin?: string;
};

export const useSectionVisibilityGuard = ({
  enabled,
  sectionRef,
  onExit,
  threshold = 0.01,
  rootMargin = '-20% 0px -20% 0px',
}: VisibilityGuardOptions) => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !sectionRef.current) return;
    const target = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          onExit();
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [enabled, sectionRef, onExit, threshold, rootMargin]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !sectionRef.current) return;
    let rafId: number | null = null;
    const checkVisibility = () => {
      rafId = null;
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
        onExit();
      }
    };
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(checkVisibility);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [enabled, sectionRef, onExit]);
};
